import { forwardRef, type HTMLAttributes, type ThHTMLAttributes, type TdHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'
import { GROUP_HEADER_CLASS } from './group-header'

/**
 * DataTable — таблица данных (`table`/`thead`/`tr.grp`/`tr.clickable` макета): заголовки столбцов,
 * групп-строки и кликабельные строки с состояниями hover/focus, активируемые с клавиатуры.
 * Семантика таблицы (роли th/td/tr) сохраняется. Цвета/границы из токенов.
 */
export const DataTable = forwardRef<HTMLTableElement, HTMLAttributes<HTMLTableElement>>(
  ({ className, ...props }, ref) => (
    <table ref={ref} className={cn('w-full border-collapse text-14', className)} {...props} />
  ),
)
DataTable.displayName = 'DataTable'

export const DataTableHead = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => <thead ref={ref} className={className} {...props} />)
DataTableHead.displayName = 'DataTableHead'

export const DataTableBody = forwardRef<
  HTMLTableSectionElement,
  HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody ref={ref} className={cn('[&>tr:last-child>td]:border-b-0', className)} {...props} />
))
DataTableBody.displayName = 'DataTableBody'

export interface DataTableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  clickable?: boolean
}

export const DataTableRow = forwardRef<HTMLTableRowElement, DataTableRowProps>(
  ({ className, clickable, onClick, onKeyDown, ...props }, ref) => (
    <tr
      ref={ref}
      tabIndex={clickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={(e) => {
        // Клавиатурой строку открывает только фокус НА САМОЙ строке: Enter на кнопке внутри строки
        // (действия в последней колонке) должен нажимать кнопку, а не открывать строку.
        if (clickable && e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          ;(e.currentTarget as HTMLTableRowElement).click()
        }
        onKeyDown?.(e)
      }}
      className={cn(
        clickable &&
          'cursor-pointer outline-none transition-colors hover:bg-hover focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
        className,
      )}
      {...props}
    />
  ),
)
DataTableRow.displayName = 'DataTableRow'

export const DataTableHeaderCell = forwardRef<
  HTMLTableCellElement,
  ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    scope="col"
    className={cn(
      'border-b border-line bg-header px-4 py-2.5 text-left text-12 font-semibold text-txt-tertiary',
      className,
    )}
    {...props}
  />
))
DataTableHeaderCell.displayName = 'DataTableHeaderCell'

export const DataTableCell = forwardRef<
  HTMLTableCellElement,
  TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td ref={ref} className={cn('border-b border-line px-4 py-3', className)} {...props} />
))
DataTableCell.displayName = 'DataTableCell'

export interface DataTableGroupRowProps extends TdHTMLAttributes<HTMLTableCellElement> {
  colSpan: number
}

/** Групп-строка таблицы — общий стиль GroupHeader, поданный через td colspan. */
export const DataTableGroupRow = forwardRef<HTMLTableCellElement, DataTableGroupRowProps>(
  ({ className, colSpan, ...props }, ref) => (
    <tr>
      <td
        ref={ref}
        colSpan={colSpan}
        className={cn(GROUP_HEADER_CLASS, 'border-b border-line', className)}
        {...props}
      />
    </tr>
  ),
)
DataTableGroupRow.displayName = 'DataTableGroupRow'
